import { Button } from "@repo/ui/button";
import { Input } from "@repo/ui/input";
import { Label } from "@repo/ui/label";
import { Switch } from "@repo/ui/switch";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import {
  fetchCrawlPolicyRequireMedia,
  fetchListingCardBudgetStatus,
  setCrawlPolicyRequireMedia,
  setListingCardWeeklyPageBudget,
} from "@/lib/api";

const QUERY_KEY = ["app-setting", "crawl_policy.require_media"];
const BUDGET_QUERY_KEY = ["listing-card-budget-status"];

/**
 * Global crawl-policy settings read by SQL sale_completeness (and so by every run's dedupe
 * trigger). Per-job switches live in Create job.
 */
export function CrawlPolicySettingsCard() {
  const { toast } = useToast();
  const { canManageJobs } = useAuth();
  const queryClient = useQueryClient();
  const requireMedia = useQuery({ queryKey: QUERY_KEY, queryFn: fetchCrawlPolicyRequireMedia });
  const save = useMutation({
    mutationFn: setCrawlPolicyRequireMedia,
    onSuccess: (value) => {
      queryClient.setQueryData(QUERY_KEY, value);
      toast({
        title: "Crawl policy saved",
        description: value
          ? "Sales without a stored photo now count as incomplete."
          : "Photos no longer count toward a complete sale.",
      });
    },
    onError: (error) => {
      toast({
        title: "Save failed",
        description: error instanceof Error ? error.message : "Could not save the setting",
        variant: "destructive",
      });
    },
  });

  const budget = useQuery({ queryKey: BUDGET_QUERY_KEY, queryFn: fetchListingCardBudgetStatus });
  const [budgetDraft, setBudgetDraft] = useState<string | null>(null);
  const saveBudget = useMutation({
    mutationFn: setListingCardWeeklyPageBudget,
    onSuccess: (pages) => {
      setBudgetDraft(null);
      queryClient.invalidateQueries({ queryKey: BUDGET_QUERY_KEY });
      toast({
        title: "For-sale page budget saved",
        description:
          pages === 0
            ? "For-sale searches are off."
            : `${pages.toLocaleString("en-AU")} pages a week.`,
      });
    },
    onError: (error) => {
      toast({
        title: "Save failed",
        description: error instanceof Error ? error.message : "Could not save the budget",
        variant: "destructive",
      });
    },
  });
  const budgetValue = budgetDraft ?? (budget.data ? String(budget.data.budget) : "");
  const budgetNumber = Number(budgetValue);
  const budgetValid = budgetValue !== "" && Number.isInteger(budgetNumber) && budgetNumber >= 0;

  return (
    <div className="card-elevated p-5 space-y-4">
      <div>
        <h3 className="font-semibold">Crawl policy</h3>
        <p className="text-sm text-muted-foreground">
          What makes a sale complete, so runs with "Skip sales we already have" do not fetch it
          again: a price from any source, a stored page from a portal or agency, and no likely-fake
          flag.
        </p>
      </div>
      <div className="flex items-start justify-between gap-4 p-4 border border-border rounded-lg">
        <div>
          <Label htmlFor="crawl-policy-require-media" className="font-medium">
            Count photos toward a complete sale
          </Label>
          <p className="text-sm text-muted-foreground mt-1">
            On: a sale without a stored photo is incomplete, so jobs with "Complete missing data"
            fetch its page again (at most once per site every 30 days). Off: photos are ignored.
            Recover photos from pages we already hold first: Adapters → an adapter → Reparse stored
            snapshots → "Also recover photos from stored pages".
          </p>
        </div>
        {requireMedia.isLoading || save.isPending ? (
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        ) : (
          <Switch
            id="crawl-policy-require-media"
            checked={requireMedia.data === true}
            disabled={!canManageJobs || requireMedia.isError}
            onCheckedChange={(checked) => save.mutate(checked)}
          />
        )}
      </div>
      {requireMedia.isError && (
        <p className="text-sm text-destructive">Could not load the crawl policy setting.</p>
      )}
      <div className="flex items-start justify-between gap-4 p-4 border border-border rounded-lg">
        <div>
          <Label htmlFor="listing-card-weekly-budget" className="font-medium">
            For-sale search pages per week
          </Label>
          <p className="text-sm text-muted-foreground mt-1">
            Jobs that follow for-sale listings until they sell read each search page's cards and
            fetch no listing pages. This caps those search pages per week (Monday, Melbourne time),
            all portals together; a search that reaches it stops until next week. 6,500 is about
            US$50 at the highest provider cost we have measured. 0 turns for-sale searches off.
          </p>
          {budget.data && (
            <p className="text-sm text-muted-foreground mt-1">
              This week: {budget.data.used.toLocaleString("en-AU")} of{" "}
              {budget.data.budget.toLocaleString("en-AU")} pages used.
            </p>
          )}
          {budget.isError && (
            <p className="text-sm text-destructive mt-1">
              Could not load the for-sale page budget.
            </p>
          )}
        </div>
        <form
          className="flex items-center gap-2 shrink-0"
          onSubmit={(event) => {
            event.preventDefault();
            if (budgetValid) saveBudget.mutate(budgetNumber);
          }}
        >
          <Input
            id="listing-card-weekly-budget"
            type="number"
            min={0}
            step={1}
            className="w-28"
            value={budgetValue}
            disabled={!canManageJobs || budget.isLoading || budget.isError}
            onChange={(event) => setBudgetDraft(event.target.value)}
          />
          <Button
            type="submit"
            size="sm"
            disabled={
              !canManageJobs || !budgetValid || budgetDraft === null || saveBudget.isPending
            }
          >
            {saveBudget.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </form>
      </div>
    </div>
  );
}
